import { ComboQuestionType } from '@common/components/questionTypes';
import { IntakePetSectionQuestionModel } from '@common/models';
import { observer } from 'mobx-react';
import React from 'react';
import { Col, Form, Image } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';

interface IProps {
    question: IntakePetSectionQuestionModel,
    selectedSectionIndex: number,
    questionIndex: number,
    updateSelectedQuestionListValue: (valueToUpdate: number, sectionIndex: number, questionIndex: number) => IntakePetSectionQuestionModel
}

export const IntakeQuestion = observer(({ question, selectedSectionIndex, questionIndex, updateSelectedQuestionListValue }: IProps) => {

    const { register, setValue, control } = useFormContext();

    return (
        <>
            <Form.Group className="mt-4">
                <Col xs={12}>
                    <span className="question">{question.name}</span>
                </Col>
                <Col xs={12}>
                    {
                        // Data entry field (1)
                        question.questionTypeId === 1 ?
                            <Form.Control type="text" disabled={question.isReadOnly} {...register(`sections.${selectedSectionIndex}.questions.${questionIndex}.stringValue`)} />
                            : null
                    }

                    {
                        // Yes/No (2)
                        question.questionTypeId === 2 ?
                            <>
                                <Controller
                                    control={control}
                                    name={`sections.${selectedSectionIndex}.questions.${questionIndex}.bitValue`}
                                    render={({ field: { onChange, value, name } }) =>
                                        <Form.Check
                                            id={`sections.${selectedSectionIndex}.questions.${questionIndex}.bitValue_yes`}
                                            inline
                                            name={name}
                                            label={question.yesOverrideText || "Yes"}
                                            type="radio"
                                            checked={value === true}
                                            disabled={question.isReadOnly}
                                            onChange={(e) => {
                                                if (e.target.checked)
                                                    onChange(true);
                                            }}
                                        />
                                    }
                                />
                                <Controller
                                    control={control}
                                    name={`sections.${selectedSectionIndex}.questions.${questionIndex}.bitValue`}
                                    render={({ field: { onChange, value, name } }) =>
                                        <Form.Check
                                            id={`sections.${selectedSectionIndex}.questions.${questionIndex}.bitValue_no`}
                                            inline
                                            name={name}
                                            label={question.noOverrideText || "No"}
                                            type="radio"
                                            checked={value === false}
                                            disabled={question.isReadOnly}
                                            onChange={(e) => {
                                                if (e.target.checked)
                                                    onChange(false);
                                            }}
                                        />
                                    }
                                />
                            </>
                            : null
                    }

                    {
                        // Radio buttons for question type (3) Allow multiples is false and not using a ddl
                        question.questionTypeId === 3 && !question.allowMultiple && !question.useMultiChoiceDropDown ?
                            <>
                                {
                                    question.intakePetSectionQuestionListValues.map((radiobutton, index, array) => (
                                        <Controller
                                            name={`sections.${selectedSectionIndex}.questions.${questionIndex}.intakePetSectionQuestionListValues`}
                                            control={control}
                                            key={`single${question.intakePetSectionQuestionId}${index}`}
                                            render={({ field: { onChange, value, name } }) => {
                                                return <Form.Check
                                                    label={radiobutton.name}
                                                    type="radio"
                                                    id={`intakeRadioGroup${question.intakePetSectionQuestionId}${index}`}
                                                    name={name}
                                                    checked={value}
                                                    value={radiobutton.name}
                                                    disabled={question.isReadOnly}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            array.forEach((val, idx) => {
                                                                if (idx !== index) {
                                                                    setValue(`sections.${selectedSectionIndex}.questions.${questionIndex}.intakePetSectionQuestionListValues.${idx}.isSelected`, false);
                                                                }
                                                            });
                                                            onChange(e.target.checked);
                                                        }
                                                    }} />
                                            }}
                                        />
                                    ))
                                }
                            </>
                            : null
                    }

                    {
                        // Show dropdown for question Type (3) if Allow Multiple is false and useMultiChoiceDropdown is true
                        question.questionTypeId === 3 && !question.allowMultiple && question.useMultiChoiceDropDown ?
                            <>
                                <Controller
                                    name={`sections.${selectedSectionIndex}.questions.${questionIndex}.selectedListValueId`}
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Form.Select className="form-control"
                                            aria-label={question.name}
                                            disabled={question.isReadOnly}
                                            value={value}
                                            onChange={(e) => {
                                                const questionToUpdate = updateSelectedQuestionListValue(parseInt(e.target.value), selectedSectionIndex, questionIndex);
                                                // Not looking for the actual intakePetSectionQuestionListValue that got updated... I'm just updating the entire question we get back from the store.
                                                // The store is updating the stores version of this question. This should help with follow up question binding and updates as well to setValues for react hook forms to then post
                                                // This will only affect the question which is ok even though the thing we are updating is a child to the question.
                                                setValue(`sections.${selectedSectionIndex}.questions.${questionIndex}`, questionToUpdate, { shouldDirty: true });
                                                onChange(e.target.value);
                                            }}
                                        >
                                            <option></option>
                                            {question.intakePetSectionQuestionListValues.map(listVal => <option key={`${listVal.intakePetSectionQuestionId}${listVal.intakePetSectionQuestionListValueId}`} value={listVal.intakePetSectionQuestionListValueId}>{listVal.name}</option>)}
                                        </Form.Select>
                                    )} />
                            </>
                            : null
                    }

                    {
                        // Show checkboxes for question type List (3) if Allow Multiple is true
                        question.questionTypeId === 3 && question.allowMultiple ?
                            <>
                                {
                                    question.intakePetSectionQuestionListValues.map((checkbox, index) => (
                                        <Controller
                                            name={`sections.${selectedSectionIndex}.questions.${questionIndex}.intakePetSectionQuestionListValues.${index}.isSelected`}
                                            control={control}
                                            key={`single${question.intakePetSectionQuestionId}${index}`}
                                            render={({ field: { onChange, value } }) => {
                                                return <Form.Check
                                                    label={checkbox.name}
                                                    type="checkbox"
                                                    id={`intakeCheckboxGroup${question.intakePetSectionQuestionId}${index}`}
                                                    name={`intakeCheckboxGroup${question.intakePetSectionQuestionId}`}
                                                    checked={value}
                                                    value={checkbox.name}
                                                    disabled={question.isReadOnly}
                                                    onChange={(e) => {
                                                        onChange(e.target.checked);
                                                    }} />
                                            }}
                                        />
                                    ))
                                }
                            </>
                            : null
                    }

                    {
                        question.questionTypeId === 4 ?
                            <Image src={question.photoUrl} />
                            : null
                    }

                    {
                        question.questionTypeId === 5 ?
                            <>
                                <ComboQuestionType
                                    questionName={`sections.${selectedSectionIndex}.questions.${questionIndex}.bitValue`}
                                    followupQuestionName={`sections.${selectedSectionIndex}.questions.${questionIndex}.stringValue`}
                                    isPreview={false}
                                    question={question}
                                    onQuestionChange={(val: boolean) =>
                                        setValue(`sections.${selectedSectionIndex}.questions.${questionIndex}.bitValue`, val)
                                    }
                                    onFollowupChange={(val: string) =>
                                        setValue(`sections.${selectedSectionIndex}.questions.${questionIndex}.stringValue`, val)
                                    }
                                />
                            </>
                            : null
                    }
                </Col>
            </Form.Group>
        </>
    )
});
